import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Camera, MapPin, Check, Truck } from "lucide-react";
import { compressImage } from "../lib/image";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicPOD() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [pod, setPod] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/p/pod/${token}`).then(r => setInfo(r.data)).catch(e => setErr(e.response?.data?.detail || "Link not valid"));
  }, [token]);

  async function onPhoto(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { alert("Photo must be under 15 MB"); return; }
    try { setPod(await compressImage(f)); }
    catch { const reader = new FileReader(); reader.onload = () => setPod(String(reader.result)); reader.readAsDataURL(f); }
  }

  async function submit() {
    if (!pod) return;
    setSaving(true); setErr("");
    try {
      await axios.post(`${API}/p/pod/${token}`, { pod_image: pod, mark_delivered: true });
      setDone(true);
    } catch (e) { setErr(e.response?.data?.detail || "Upload failed"); }
    finally { setSaving(false); }
  }

  if (err && !info) return <div className="min-h-screen flex items-center justify-center p-6 bg-[#FDFCF8] text-center text-[#8A2526]" data-testid="public-pod-error">{err}</div>;
  if (!info) return <div className="min-h-screen flex items-center justify-center text-[#68665E]">Loading...</div>;

  return (
    <div className="min-h-screen bg-[#FDFCF8]" data-testid="public-pod-page">
      <header className="bg-[#1A1A18] text-white p-4 flex items-center gap-3">
        <img src="/ito-logo.png" alt="ITO" className="w-10 h-10 object-contain" />
        <div>
          <div className="font-headings font-bold text-sm leading-tight">INDIAN TRANSPORT</div>
          <div className="text-[10px] uppercase tracking-widest opacity-70">Driver POD Upload</div>
        </div>
      </header>

      <div className="p-5 space-y-4 max-w-md mx-auto">
        <div className="card-surface p-4">
          <div className="text-xs uppercase tracking-wide text-[#68665E]">LR Number</div>
          <div className="font-mono text-lg font-bold mt-1" data-testid="public-pod-lr-number">{info.lr_number}</div>
          <div className="mt-4">
            <div className="text-xs uppercase tracking-wide text-[#68665E]">Deliver to</div>
            <div className="font-headings text-lg font-bold mt-1">{info.consignee_name}</div>
            <div className="text-sm text-[#68665E]">{info.consignee_address || "—"}</div>
            <div className="flex items-center gap-2 mt-2 text-sm font-medium"><MapPin className="w-4 h-4 text-[#C84B31]" />{info.from_location} → {info.to_location}</div>
            {info.delivery_maps_url && (
              <a href={info.delivery_maps_url} target="_blank" rel="noreferrer" className="btn-secondary w-full mt-3 justify-center" data-testid="public-pod-navigate"><MapPin className="w-4 h-4" /> Navigate in Maps</a>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4 text-sm border-t border-[#E5E3DB] pt-3">
            <div><div className="text-xs text-[#68665E]">Goods</div><div className="font-medium">{info.goods_description}</div></div>
            <div><div className="text-xs text-[#68665E]">Vehicle</div><div className="font-mono">{info.vehicle_number || "—"}</div></div>
          </div>
        </div>

        {info.already_uploaded || done ? (
          <div className="bg-[#EAF2ED] text-[#2D523B] p-6 text-center" data-testid="public-pod-success">
            <Check className="w-8 h-8 mx-auto mb-2" />
            <div className="font-medium">{done ? "POD uploaded. Thanks for the delivery!" : "POD already uploaded for this LR."}</div>
          </div>
        ) : (
          <>
            {pod ? (
              <div className="relative">
                <img src={pod} alt="POD" className="w-full max-h-96 object-cover border border-[#E5E3DB]" />
                <button onClick={() => setPod("")} className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1">Retake</button>
              </div>
            ) : (
              <label className="card-surface p-10 flex flex-col items-center cursor-pointer border-dashed">
                <Camera className="w-12 h-12 text-[#C84B31] mb-3" />
                <div className="font-medium">Tap to capture POD</div>
                <div className="text-xs text-[#68665E] mt-1">Signed POD or delivered photo</div>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid="public-pod-camera" />
              </label>
            )}
            {err && <div className="badge-error w-full justify-center py-2 mt-2">{err}</div>}
            <button onClick={submit} disabled={!pod || saving} className="btn-primary w-full py-4 text-base" data-testid="public-pod-submit">{saving ? "Uploading..." : "Mark Delivered"}</button>
          </>
        )}
      </div>
    </div>
  );
}
